/**
 * CSS variable tokens for each theme.
 *
 * classic_neon → mirrors the current (Preset 1) frontend so it remains byte-safe.
 * nocturne     → Preset 2 "Nocturne Vault" — premium dark gaming client UI.
 *                Deep graphite + cinematic gold + emerald success.
 *                Animated, layered, money-forward — not flat editorial.
 */

export const classicNeonTheme = {
    "--preset-id": "classic_neon",
    "--preset-bg": "#0a0a0f",
    "--preset-bg-gradient-from": "#111827",
    "--preset-bg-gradient-to": "#000000",
    "--preset-surface": "rgba(255,255,255,0.04)",
    "--preset-surface-border": "rgba(255,255,255,0.08)",
    "--preset-text-primary": "#ffffff",
    "--preset-text-secondary": "#9ca3af",
    "--preset-text-muted": "#6b7280",
    "--preset-accent": "#8b5cf6",
    "--preset-accent-2": "#06b6d4",
    "--preset-accent-3": "#10b981",
    "--preset-danger": "#ef4444",
    "--preset-warning": "#f59e0b",
    "--preset-success": "#10b981",
    "--preset-radius-sm": "0.5rem",
    "--preset-radius-md": "0.75rem",
    "--preset-radius-lg": "1rem",
    "--preset-shadow-card": "0 10px 24px -12px rgba(139, 92, 246, 0.35)",
    "--preset-font-body":
        "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
    "--preset-font-display": "'Inter', system-ui, -apple-system, sans-serif",
    "--preset-font-mono": "ui-monospace, SFMono-Regular, Menlo, monospace",
};

/**
 * Nocturne Vault — Preset 2
 *
 * Identity: premium dark gaming client. Layered graphite cards on near-black.
 * Cinematic gold for value/CTAs. Emerald for success/wins. Deep red for failure.
 *
 * - Base:               #08090C (deeper than v1's 0E0F13 — gives gold real punch)
 * - Surface:            #14161D (cards/panels)
 * - Surface 2:          #1A1D26 (raised inner)
 * - Hairlines:          ivory @ 8% / 16%
 * - Text:               ivory F1ECE4, secondary 9FA0A6, muted 6C6D74
 * - Accent (gold):      #D4A24C  hover #E8B968   ink #08090C
 * - Success (emerald):  #2FB37A  surface tint    ring #2FB37A33
 * - Danger:             #D24846  (warmer than crimson)
 * - Radius:             10/14/18 (softer than v1's 6/10/14 — cards feel premium not industrial)
 */
export const nocturneTheme = {
    "--preset-id": "nocturne",

    /* Backgrounds */
    "--preset-bg": "#08090C",
    "--preset-bg-2": "#0C0E13",
    "--preset-bg-gradient-from": "#08090C",
    "--preset-bg-gradient-to": "#08090C",
    "--preset-surface": "#14161D",
    "--preset-surface-2": "#1A1D26",
    "--preset-surface-3": "#22252F",
    "--preset-surface-border": "rgba(241, 236, 228, 0.07)",
    "--preset-surface-border-strong": "rgba(241, 236, 228, 0.16)",

    /* Text */
    "--preset-text-primary": "#F1ECE4",
    "--preset-text-secondary": "#9FA0A6",
    "--preset-text-muted": "#6C6D74",

    /* Accents */
    "--preset-accent": "#D4A24C",
    "--preset-accent-hover": "#E8B968",
    "--preset-accent-soft": "rgba(212, 162, 76, 0.14)",
    "--preset-accent-glow": "rgba(212, 162, 76, 0.32)",
    "--preset-accent-ink": "#08090C",

    /* Status colors */
    "--preset-success": "#2FB37A",
    "--preset-success-soft": "rgba(47, 179, 122, 0.14)",
    "--preset-success-glow": "rgba(47, 179, 122, 0.30)",
    "--preset-warning": "#D4A24C",
    "--preset-warning-soft": "rgba(212, 162, 76, 0.14)",
    "--preset-danger": "#D24846",
    "--preset-danger-soft": "rgba(210, 72, 70, 0.14)",
    "--preset-info": "#6FA8DC",
    "--preset-info-soft": "rgba(111, 168, 220, 0.14)",

    /* Compat aliases */
    "--preset-accent-2": "#F1ECE4",
    "--preset-accent-3": "#2FB37A",

    /* Radii */
    "--preset-radius-sm": "8px",
    "--preset-radius-md": "12px",
    "--preset-radius-lg": "16px",
    "--preset-radius-xl": "22px",

    /* Shadows */
    "--preset-shadow-card": "0 10px 32px -16px rgba(0, 0, 0, 0.55)",
    "--preset-shadow-elevated":
        "0 18px 48px -20px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(241, 236, 228, 0.05)",
    "--preset-shadow-gold": "0 12px 32px -14px rgba(212, 162, 76, 0.55)",
    "--preset-shadow-success": "0 12px 32px -14px rgba(47, 179, 122, 0.45)",

    /* Fonts */
    "--preset-font-body":
        "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
    "--preset-font-display":
        "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
    "--preset-font-mono":
        "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

// Backward-compat alias (old `preset_2_legacy` points at this so nothing breaks)
export const midnightAmberTheme = nocturneTheme;
